import React, { useState, useEffect } from 'react';
import { Button } from '../components/Button';
import { Header } from '../components/Header';
import { apiClient } from '../api/client';
import { useAppStore } from '../store/useAppStore';
import './ResetPasswordScreen.css';

export const ResetPasswordScreen: React.FC = () => {
  const { setCurrentPage } = useAppStore();
  const [token, setToken] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Извлекаем токен из URL при монтировании
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = params.get('token');
    
    if (!tokenFromUrl) {
      setError('Токен восстановления не найден в URL');
      setIsVerifying(false);
      return;
    }

    setToken(tokenFromUrl);

    // Проверяем валидность токена
    const verifyToken = async () => {
      try {
        const result = await apiClient.verifyResetToken(tokenFromUrl);
        
        if (result.valid) {
          setTokenValid(true);
          console.log('✅ Token is valid');
        } else {
          setError(result.error || 'Недействительный или истекший токен восстановления');
          setTokenValid(false);
        }
      } catch (error: any) {
        console.error('Token verification error:', error);
        setError('Ошибка проверки токена');
        setTokenValid(false);
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Валидация
    if (!password || !confirmPassword) {
      setError('Заполните все поля');
      return;
    }
    
    if (password.length < 6) {
      setError('Пароль должен содержать минимум 6 символов');
      return;
    }

    if (password !== confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }

    if (!token) {
      setError('Токен не найден');
      return;
    }

    try {
      setIsProcessing(true);
      
      await apiClient.resetPassword(token, password);
      
      setSuccess(true);
      console.log('✅ Password reset successful');
    } catch (error: any) {
      console.error('Password reset error:', error);
      setError(error.message || 'Ошибка сброса пароля');
    } finally {
      setIsProcessing(false);
    }
  };

  // Загрузка
  if (isVerifying) {
    return (
      <div className="reset-password-screen">
        <div className="reset-password-container">
          <div className="loading-message">
            <div className="spinner" />
            <p>Проверка ссылки восстановления...</p>
          </div>
        </div>
      </div>
    );
  }

  // Недействительный токен
  if (!tokenValid) {
    return (
      <div className="reset-password-screen">
        <div className="reset-password-container">
          <div className="error-state">
            <div className="error-icon">
              <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="30" cy="30" r="30" fill="rgba(255, 59, 48, 0.1)" />
                <path d="M25 25L35 35M35 25L25 35" stroke="#FF3B30" strokeWidth="4" strokeLinecap="round"/>
              </svg>
            </div>
            
            <h2 className="error-title">Ссылка недействительна</h2>
            
            <p className="error-text">
              {error || 'Эта ссылка восстановления пароля недействительна или истекла'}
            </p>
            
            <p className="error-hint">
              Ссылки действительны в течение 1 часа. Вы можете запросить новую ссылку.
            </p>

            <div className="error-actions">
              <Button
                variant="primary"
                onClick={() => {
                  window.location.href = '/request-password-reset';
                }}
                style={{ width: '100%', padding: '14px' }}
              >
                Запросить новую ссылку
              </Button>
              
              <button
                className="link-button"
                onClick={() => {
                  window.location.href = '/';
                }}
              >
                На главную
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Успех
  if (success) {
    return (
      <div className="reset-password-screen">
        <div className="reset-password-container">
          <div className="success-message">
            <div className="success-icon">
              <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="30" cy="30" r="30" fill="url(#paint0_linear_success)" />
                <path d="M20 30L27 37L42 22" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                <defs>
                  <linearGradient id="paint0_linear_success" x1="0" y1="0" x2="60" y2="60" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#34C759" />
                    <stop offset="1" stopColor="#30D158" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            
            <h2 className="success-title">Пароль изменен!</h2>
            
            <p className="success-text">
              Ваш пароль успешно изменен. Теперь вы можете войти в приложение с новым паролем.
            </p>

            <div className="success-actions">
              <Button
                variant="primary"
                onClick={() => {
                  // Очищаем сохраненный токен и перенаправляем на логин
                  localStorage.removeItem('auth_token');
                  localStorage.removeItem('user_id');
                  window.location.href = '/';
                }}
                style={{ width: '100%', padding: '14px' }}
              >
                Войти
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Форма установки нового пароля
  return (
    <div className="reset-password-screen">
      <div className="reset-password-container">
        <Header title="Новый пароль" onBack={() => setCurrentPage('home')} />
        <p style={{ textAlign: 'center', marginTop: '16px', marginBottom: '24px', color: 'var(--color-text-secondary)' }}>
          Придумайте надежный пароль для вашего аккаунта
        </p>

        <form onSubmit={handleSubmit} className="reset-password-form">
          <div className="form-group">
            <label htmlFor="password" className="form-label">
              Новый пароль
            </label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Минимум 6 символов"
                className="form-input"
                disabled={isProcessing}
                autoComplete="new-password"
                autoFocus
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 3L21 21M10.5 10.677A2 2 0 1013.323 13.5M7.362 7.561C5.68 8.74 4.279 10.42 3 12c1.889 2.991 5.282 6 9 6 1.55 0 3.043-.523 4.395-1.35M12 6c4.008 0 6.701 3.158 9 6a15.66 15.66 0 01-1.078 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5C7.5 5 3.73 7.61 1 12c2.73 4.39 6.5 7 11 7s8.27-2.61 11-7c-2.73-4.39-6.5-7-11-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">
              Подтвердите пароль
            </label>
            <div className="password-input-wrapper">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Введите пароль еще раз"
                className="form-input"
                disabled={isProcessing}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? 'Скрыть пароль' : 'Показать пароль'}
              >
                {showConfirmPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 3L21 21M10.5 10.677A2 2 0 1013.323 13.5M7.362 7.561C5.68 8.74 4.279 10.42 3 12c1.889 2.991 5.282 6 9 6 1.55 0 3.043-.523 4.395-1.35M12 6c4.008 0 6.701 3.158 9 6a15.66 15.66 0 01-1.078 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5C7.5 5 3.73 7.61 1 12c2.73 4.39 6.5 7 11 7s8.27-2.61 11-7c-2.73-4.39-6.5-7-11-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Индикатор надежности пароля */}
          {password && (
            <div className="password-strength">
              <div className="strength-label">
                Надежность пароля:
              </div>
              <div className="strength-bar">
                <div 
                  className={`strength-fill ${
                    password.length >= 12 ? 'strength-strong' : 
                    password.length >= 8 ? 'strength-medium' : 
                    'strength-weak'
                  }`}
                  style={{ 
                    width: `${Math.min((password.length / 12) * 100, 100)}%` 
                  }}
                />
              </div>
              <div className="strength-text">
                {password.length >= 12 ? 'Отличный пароль' : 
                 password.length >= 8 ? 'Хороший пароль' : 
                 'Слабый пароль'}
              </div>
            </div>
          )}

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            disabled={isProcessing || !password || !confirmPassword}
            style={{ width: '100%', padding: '14px' }}
          >
            {isProcessing ? 'Сохранение...' : 'Сохранить новый пароль'}
          </Button>
        </form>
      </div>
    </div>
  );
};

